import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Peer from 'peerjs';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTier } from '../hooks/useTier';
import { db } from '../config/firebase';
import { collection, doc, setDoc, onSnapshot, deleteDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import AdPlayer from '../components/AdPlayer';
import UserProfile from '../components/UserProfile';
import UserSettings from '../components/UserSettings';
import ChatRoomTour from '../components/ChatRoomTour';

export default function ChatRoom() {
  const { roomId } = useParams();
  const { user } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const { isPremium } = useTier();
  const navigate = useNavigate();

  const [showAd, setShowAd] = useState(true);
  const [adCompleted, setAdCompleted] = useState(false);

  const [showProfile, setShowProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showTour, setShowTour] = useState(false);

  const [peer, setPeer] = useState(null);
  const [myPeerId, setMyPeerId] = useState('');
  const [participants, setParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [dataConnections, setDataConnections] = useState({});
  const [typingUsers, setTypingUsers] = useState({});

  const dataConnectionsRef = useRef({});
  const peerRef = useRef(null);
  const connectingPeersRef = useRef(new Set());
  const typingTimeoutRef = useRef({});

  useEffect(() => {
    const peerId = `${roomId}-${Date.now()}`;
    const newPeer = new Peer(peerId, {
      host: '0.peerjs.com',
      secure: true,
      port: 443,
      path: '/'
    });

    let unsubscribe = null;

    newPeer.on('open', async (id) => {
      setMyPeerId(id);
      setPeer(newPeer);
      peerRef.current = newPeer;

      // Clean up stale peer entries (older than 1 minute)
      try {
        const peersRef = collection(db, 'chatRooms', roomId, 'peers');
        const snapshot = await getDocs(peersRef);
        const now = Date.now();
        const cleanupPromises = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          const joinedAt = data.joinedAt?.toMillis?.() || 0;
          const age = now - joinedAt;

          if (age > 60000) {
            console.log('Cleaning up stale peer:', doc.id);
            cleanupPromises.push(deleteDoc(doc.ref));
          }
        });

        await Promise.all(cleanupPromises);
      } catch (err) {
        console.error('Error cleaning up stale peers:', err);
      }

      // Add this peer to the chat room in Firestore
      const peerDocRef = doc(db, 'chatRooms', roomId, 'peers', id);
      await setDoc(peerDocRef, {
        peerId: id,
        userId: user?.uid || 'anonymous',
        userName: user?.email || 'Guest',
        joinedAt: serverTimestamp()
      });

      // Listen for other peers in the chat room
      const peersRef = collection(db, 'chatRooms', roomId, 'peers');
      unsubscribe = onSnapshot(peersRef, (snapshot) => {
        const currentParticipants = [];

        snapshot.forEach((doc) => {
          const peerData = doc.data();
          if (peerData.peerId !== id) {
            currentParticipants.push(peerData);
          }
        });

        setParticipants(currentParticipants);

        snapshot.docChanges().forEach((change) => {
          const peerData = change.doc.data();

          if (change.type === 'added' && peerData.peerId !== id) {
            const joinedAt = peerData.joinedAt?.toMillis?.() || 0;
            const age = Date.now() - joinedAt;

            if (age > 120000) {
              console.log('Ignoring stale peer:', peerData.peerId);
              return;
            }

            console.log('New peer detected:', peerData.peerId);

            // Only initiate connection if our peer ID is "greater"
            if (id > peerData.peerId) {
              console.log('Initiating data connection (higher ID)');
              setTimeout(() => {
                createDataConnection(peerData.peerId);
              }, 1000);
            } else {
              console.log('Waiting for incoming connection (lower ID)');
            }
          }

          if (change.type === 'removed' && peerData.peerId !== id) {
            removeParticipant(peerData.peerId);
          }
        });
      });
    });

    // Handle incoming data connections
    newPeer.on('connection', (conn) => {
      console.log('Incoming data connection from:', conn.peer);

      conn.on('open', () => {
        console.log('Incoming data connection opened from', conn.peer);
        dataConnectionsRef.current[conn.peer] = conn;
        setDataConnections(prev => ({ ...prev, [conn.peer]: conn }));
      });

      conn.on('data', (data) => {
        handleIncomingData(data, conn.peer);
      });

      conn.on('close', () => {
        console.log('Incoming data connection closed from', conn.peer);
        delete dataConnectionsRef.current[conn.peer];
        setDataConnections(prev => {
          const newConns = { ...prev };
          delete newConns[conn.peer];
          return newConns;
        });
      });

      conn.on('error', (err) => {
        console.error('Incoming data connection error from', conn.peer, err);
      });
    });

    newPeer.on('error', (err) => {
      console.error('PeerJS error:', err);
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }

      // Remove this peer from Firestore
      if (myPeerId) {
        const peerDocRef = doc(db, 'chatRooms', roomId, 'peers', myPeerId);
        deleteDoc(peerDocRef).catch(err => console.error('Error removing peer:', err));
      }

      newPeer.destroy();
    };
  }, [roomId, user]);

  const createDataConnection = (remotePeerId) => {
    const currentPeer = peerRef.current;

    if (!currentPeer) {
      console.log('Peer not ready for data connection');
      return;
    }

    if (dataConnectionsRef.current[remotePeerId]) {
      console.log('Data connection already exists with', remotePeerId);
      return;
    }

    if (connectingPeersRef.current.has(remotePeerId)) {
      console.log('Already connecting to', remotePeerId);
      return;
    }

    connectingPeersRef.current.add(remotePeerId);
    console.log('Creating data connection with', remotePeerId);

    const conn = currentPeer.connect(remotePeerId, { reliable: true });

    conn.on('open', () => {
      console.log('Data connection opened with', remotePeerId);
      dataConnectionsRef.current[remotePeerId] = conn;
      setDataConnections(prev => ({ ...prev, [remotePeerId]: conn }));
      connectingPeersRef.current.delete(remotePeerId);
    });

    conn.on('data', (data) => {
      handleIncomingData(data, remotePeerId);
    });

    conn.on('close', () => {
      console.log('Data connection closed with', remotePeerId);
      delete dataConnectionsRef.current[remotePeerId];
      setDataConnections(prev => {
        const newConns = { ...prev };
        delete newConns[remotePeerId];
        return newConns;
      });
      connectingPeersRef.current.delete(remotePeerId);
    });

    conn.on('error', (err) => {
      console.error('Data connection error with', remotePeerId, err);
      connectingPeersRef.current.delete(remotePeerId);

      // Retry once
      setTimeout(() => {
        console.log('Retrying connection to', remotePeerId);
        connectingPeersRef.current.delete(remotePeerId);
        createDataConnection(remotePeerId);
      }, 2000);
    });
  };

  const handleIncomingData = (data, peerId) => {
    if (data.type === 'chat') {
      setMessages(prev => [...prev, {
        sender: data.sender,
        message: data.message,
        timestamp: data.timestamp
      }]);
    } else if (data.type === 'typing') {
      setTypingUsers(prev => ({ ...prev, [peerId]: data.sender }));

      // Clear existing timeout
      if (typingTimeoutRef.current[peerId]) {
        clearTimeout(typingTimeoutRef.current[peerId]);
      }

      // Set new timeout to clear typing indicator
      typingTimeoutRef.current[peerId] = setTimeout(() => {
        setTypingUsers(prev => {
          const newTyping = { ...prev };
          delete newTyping[peerId];
          return newTyping;
        });
      }, 3000);
    }
  };

  const removeParticipant = (peerId) => {
    setParticipants(prev => prev.filter(p => p.peerId !== peerId));
    delete dataConnectionsRef.current[peerId];
    setDataConnections(prev => {
      const newConns = { ...prev };
      delete newConns[peerId];
      return newConns;
    });
  };

  const handleSendMessage = (e) => {
    e.preventDefault();

    if (!messageInput.trim()) return;

    const messageData = {
      type: 'chat',
      sender: user?.email || 'Guest',
      message: messageInput.trim(),
      timestamp: Date.now()
    };

    // Add to own messages
    setMessages(prev => [...prev, messageData]);

    // Send to all connected peers
    Object.values(dataConnectionsRef.current).forEach(conn => {
      if (conn.open) {
        conn.send(messageData);
      }
    });

    setMessageInput('');
  };

  const handleTyping = () => {
    const typingData = {
      type: 'typing',
      sender: user?.email || 'Guest'
    };

    // Send typing indicator to all connected peers
    Object.values(dataConnectionsRef.current).forEach(conn => {
      if (conn.open) {
        conn.send(typingData);
      }
    });
  };

  const copyRoomLink = () => {
    const link = window.location.href;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAdComplete = () => {
    setAdCompleted(true);
    setShowAd(false);
  };


  const leaveRoom = () => {
    if (peer) {
      peer.destroy();
    }
    navigate('/dashboard');
  };

  // Show ad player before joining chat room (unless premium)
  if (showAd && !isPremium && !adCompleted) {
    return <AdPlayer onAdComplete={handleAdComplete} userIsPremium={isPremium} />;
  }

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const typingUsersList = Object.values(typingUsers);

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <header className="bg-[#1c1c1e]/95 backdrop-blur-2xl border-b border-white/10 px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <div>
            <h1 className="text-white text-base font-semibold">QuickMeet Chat</h1>
            <p className="text-[#8e8e93] text-xs">Chat Room · {roomId}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* P2P Connection Indicator */}
          {participants.length > 0 && (
            <div data-tour="encryption" className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-full backdrop-blur-xl">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-green-400 text-xs font-medium">End-to-End Encrypted</span>
              <svg className="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          )}

          {/* Profile Button */}
          <button
            onClick={() => setShowProfile(true)}
            className="p-2 rounded-full hover:bg-white/10 transition-all duration-200"
            title="Profile"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </button>

          {/* Tour Button */}
          <button
            onClick={() => setShowTour(true)}
            className="p-2 rounded-full hover:bg-white/10 transition-all duration-200"
            title="Take a Tour"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>

          {/* Dark Mode Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-white/10 transition-all duration-200"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          <button
            onClick={copyRoomLink}
            data-tour="copy-link"
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full text-sm font-medium transition-all backdrop-blur-xl border border-white/10"
          >
            {copied ? '✓ Copied' : 'Copy Link'}
          </button>
          <button
            onClick={leaveRoom}
            className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-full text-sm font-medium transition-all backdrop-blur-xl border border-red-500/30"
          >
            Leave
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col order-2 md:order-1">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            {messages.length === 0 ? (
              <div className="h-full flex items-center justify-center animate-fade-in">
                <div className="text-center max-w-md">
                  <div className="w-20 h-20 bg-[#2c2c2e] rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-white/5 animate-pulse">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h3 className="text-white text-2xl font-semibold mb-3">
                    {participants.length === 0 ? 'Waiting for participants' : 'Start the conversation'}
                  </h3>
                  <p className="text-[#8e8e93] mb-6 leading-relaxed">
                    {participants.length === 0
                      ? 'Share the chat room link to invite others'
                      : 'Type a message below to begin chatting'}
                  </p>

                  {/* P2P Privacy Notice */}
                  <div className="px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-2xl">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <span className="text-green-400 text-sm font-semibold">100% Private & Secure</span>
                    </div>
                    <p className="text-[#8e8e93] text-xs leading-relaxed">
                      Direct peer-to-peer connection. Messages are never stored on servers.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, index) => {
                  const isOwnMessage = msg.sender === (user?.email || 'Guest');
                  return (
                    <div key={index} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs lg:max-w-md ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                        {!isOwnMessage && (
                          <p className="text-[#8e8e93] text-xs mb-1 px-4">{msg.sender}</p>
                        )}
                        <div className={`px-4 py-2.5 rounded-2xl ${
                          isOwnMessage
                            ? 'bg-gradient-to-r from-[#06c] to-[#0055d4] text-white'
                            : 'bg-[#1c1c1e] text-white border border-white/10'
                        }`}>
                          <p className="break-words">{msg.message}</p>
                          <p className={`text-xs mt-1 ${isOwnMessage ? 'text-white/70' : 'text-[#8e8e93]'}`}>
                            {formatTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Typing Indicator */}
            {typingUsersList.length > 0 && (
              <div className="flex justify-start">
                <div className="bg-[#1c1c1e] px-4 py-2.5 rounded-2xl border border-white/10">
                  <p className="text-[#8e8e93] text-sm">
                    {typingUsersList[0]} is typing...
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="border-t border-white/10 bg-[#1c1c1e]/95 backdrop-blur-2xl px-6 py-4">
            <form onSubmit={handleSendMessage} className="flex gap-3" data-tour="message-input">
              <input
                type="text"
                value={messageInput}
                onChange={(e) => {
                  setMessageInput(e.target.value);
                  handleTyping();
                }}
                placeholder="Type a message..."
                maxLength={500}
                className="flex-1 bg-[#2c2c2e] text-white px-4 py-3 rounded-2xl border border-white/10 focus:outline-none focus:border-[#06c] transition-colors placeholder-[#8e8e93]"
              />
              <button
                type="submit"
                disabled={!messageInput.trim()}
                className="px-6 py-3 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-2xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send
              </button>
            </form>
            <p className="text-[#8e8e93] text-xs mt-2 text-center">
              End-to-end encrypted • Messages not saved
            </p>
          </div>
        </div>

        {/* Participants Sidebar */}
        <div className="w-full md:w-72 border-t md:border-t-0 md:border-l border-white/10 bg-[#1c1c1e]/50 backdrop-blur-2xl order-1 md:order-2" data-tour="participants">
          <div className="p-6">
            <h3 className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Participants ({participants.length + 1})
            </h3>
            <div className="space-y-2">
              {/* Current User */}
              <div className="flex items-center gap-3 px-3 py-2 bg-[#2c2c2e] rounded-xl border border-white/10">
                <div className="w-8 h-8 bg-gradient-to-r from-[#06c] to-[#0055d4] rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-semibold">
                    {(user?.email || 'Guest')[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {user?.email || 'Guest'} (You)
                  </p>
                </div>
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>

              {/* Other Participants */}
              {participants.map((participant) => (
                <div key={participant.peerId} className="flex items-center gap-3 px-3 py-2 bg-[#2c2c2e] rounded-xl border border-white/10">
                  <div className="w-8 h-8 bg-[#3c3c3e] rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {participant.userName?.[0]?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {participant.userName}
                    </p>
                  </div>
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                </div>
              ))}
            </div>

            {participants.length === 0 && (
              <div className="mt-6 text-center">
                <p className="text-[#8e8e93] text-sm mb-4">
                  Waiting for participants to join
                </p>
                <button
                  onClick={copyRoomLink}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm font-medium transition-all border border-white/10 w-full"
                >
                  {copied ? '✓ Link Copied' : 'Share Invite Link'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Modal */}
      {showProfile && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50"
          onClick={() => setShowProfile(false)}
        >
          <div
            className="w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                onClick={() => setShowProfile(false)}
                className="absolute -top-3 -right-3 z-10 p-2 bg-white dark:bg-[#2c2c2e] rounded-full shadow-lg hover:bg-[#f5f5f7] dark:hover:bg-[#3c3c3e] transition"
              >
                <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <UserProfile />
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50"
          onClick={() => setShowSettings(false)}
        >
          <div
            className="w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                onClick={() => setShowSettings(false)}
                className="absolute -top-3 -right-3 z-10 p-2 bg-white dark:bg-[#2c2c2e] rounded-full shadow-lg hover:bg-[#f5f5f7] dark:hover:bg-[#3c3c3e] transition"
              >
                <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <UserSettings />
            </div>
          </div>
        </div>
      )}

      {/* Tour */}
      <ChatRoomTour isOpen={showTour} onClose={() => setShowTour(false)} />
    </div>
  );
}
